import { useMemo } from "react";
import type { ButtonSize } from "@design-system/Button/Button";
import { Picker } from "@design-system/Picker/Picker.lib";
import type {
    PickerItemData,
    PickerItemValue,
} from "@design-system/Picker/Picker.types";

/** A knob option: a bare value shown as its own label, or a value with a label. */
export type PlaygroundOption<T extends PickerItemValue> =
    | T
    | { value: T; label: string };

const isLabelledOption = <T extends PickerItemValue>(
    option: PlaygroundOption<T>,
): option is { value: T; label: string } => typeof option === "object";

const toPickerItem = <T extends PickerItemValue>(
    option: PlaygroundOption<T>,
): PickerItemData<T> =>
    isLabelledOption(option)
        ? option
        : { value: option, label: String(option) };

/**
 * @purpose The knob every showcase aside uses to pick one of a fixed set of
 * values: a primary Button-triggered `Picker.Select` that always holds a value.
 * `searchPlaceholder` adds a search bar to the popup for long option lists.
 */
export const PlaygroundSelect = <T extends PickerItemValue>({
    options,
    value,
    onChange,
    size = "md",
    disabled = false,
    searchPlaceholder,
    "aria-labelledby": ariaLabelledby,
}: {
    options: ReadonlyArray<PlaygroundOption<T>>;
    value: T;
    onChange: (value: T) => void;
    size?: ButtonSize;
    disabled?: boolean;
    searchPlaceholder?: string;
    "aria-labelledby"?: string;
}) => {
    const items = useMemo(() => options.map(toPickerItem), [options]);

    return (
        <Picker.Select<T>
            items={items}
            value={value}
            disabled={disabled}
            onValueChange={(nextValue) => {
                if (nextValue !== null) {
                    onChange(nextValue);
                }
            }}
            trigger={
                <Picker.Button
                    variant="primary"
                    size={size}
                    aria-labelledby={ariaLabelledby}
                />
            }
            {...(searchPlaceholder === undefined
                ? {}
                : { searchable: true, searchPlaceholder })}
        />
    );
};
